import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  clearSessionData,
  selectDeployBlockedUnpaid,
  sessionFetchInvoiceStatus,
} from '@/store/slices/session';
import { shouldReadInvoiceStatus } from '@/utils/deploy-gate';
import { DevicePreview } from '../../components/DevicePreview';
import { SparkleIcon, RocketIcon, GlobeIcon, DiplomaIcon, CheckIcon, ArrowLeftIcon, ExternalLinkIcon } from '../../components/icons';
import { useDeployStatusPhrases, useTypewriterStatus } from '../../lib/useTypewriterStatus';
import { useCustomizer } from '../../store/customizer';
import {
  Shell,
  HeaderRow,
  HeaderLeft,
  Avatar,
  BackButton,
  HeaderTitle,
  HeaderRight,
  LiveRow,
  LiveDot,
  LiveText,
  Body,
  PreviewCol,
  PreviewLabel,
  Sidebar,
  SideHeader,
  SideHeaderRow,
  RecordDot,
  LabelGroup,
  SideHeaderLabel,
  Pct,
  Track,
  Fill,
  SideBody,
  SummaryList,
  SummaryRow,
  Divider,
  SumLabel,
  SumValueRow,
  SumValue,
  FlagImg,
  SumLogo,
  DoneHeading,
  TypingHeading,
  Steps,
  StepItem,
  StepCheck,
  StepText,
  Section,
  SideFooter,
  Cta,
  CtaLabel,
  PreviewCta,
  MobileBody,
  MHeading,
  MCard,
  MCardLabel,
  MCardValueRow,
  MCardValue,
  MProgressWrap,
  MList,
  MPrimary,
  MSecondary,
  MBtnLabel,
  MDoneRoot,
  MDonePreview,
  MDoneFooter,
  UnpaidRow,
  UnpaidIcon,
  UnpaidText,
} from './deploy.styles';

/**
 * Whether onboarding must hold the launch back because the session's invoice is not paid.
 *
 * The websocket (`invoice_update`) only reports while a page is open, so an owner returning in
 * a fresh tab has no live signal — the status is READ once on mount to seed it, and the
 * websocket keeps it current from then on. Fails open: see `selectDeployBlockedUnpaid`.
 */
function useDeployBlockedUnpaid(): boolean {
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector((s) => s.session.sessionId);
  const sessionLoaded = useAppSelector((s) => s.session.loaded);
  const invoiceStatus = useAppSelector((s) => s.session.invoiceStatus);
  const asked = React.useRef(false);

  React.useEffect(() => {
    // Once per mount, and only while the status is still unknown — the websocket owns it after.
    //
    // `force` because this read must not be answered by the checkout's once-per-session latch. The
    // configurator probes the invoice at flow ENTRY, before one exists, and that empty read marks
    // the status "settled" for the rest of the page. An owner who goes straight from a SPEI
    // confirmation into onboarding without a reload would then reach this gate with the status
    // still unknown — and unknown FAILS OPEN, launching a brand whose transfer never arrived.
    //
    // Held until `/session/load` has landed: the answer is only believed when it can be attributed
    // to the session's own invoice, and that id comes with the envelope. See
    // `shouldReadInvoiceStatus`.
    if (!sessionId) return;
    if (!shouldReadInvoiceStatus({ alreadyAsked: asked.current, sessionLoaded, statusKnown: Boolean(invoiceStatus) }))
      return;
    asked.current = true;
    void dispatch(sessionFetchInvoiceStatus({ sessionId, force: true }));
  }, [dispatch, sessionId, sessionLoaded, invoiceStatus]);

  // Hold the launch while the read is IN FLIGHT too. `isDeployBlockedUnpaid` fails open on an
  // unknown status, which is right as a steady state (never strand a paying customer) but wrong
  // for the second or two before the answer arrives: an owner who clicks inside that window
  // launches on "unknown" when the answer was about to be "pending".
  const reading = useAppSelector((s) => s.session.invoiceStatusLoading);
  return useAppSelector(selectDeployBlockedUnpaid) || reading;
}

/** Explains why the launch button is being held back, above the CTA. */
const UnpaidNotice = (): React.ReactElement => (
  <UnpaidRow role="status">
    <UnpaidIcon>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
        <path
          d="M12 7.5V12l3 2"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </UnpaidIcon>
    <UnpaidText
      text="We're still waiting for your bank transfer to arrive. You can finish setting everything up now, and launch as soon as the payment lands. We'll email you the moment it does."
      tag="paragraph"
    />
  </UnpaidRow>
);

const DASHBOARD_PATH = '/account/home';

/**
 * Floor the DISPLAYED deploy progress while deploying. The phase flips to
 * "deploying" the instant `startDeploy` fires, but `deploy.progress` stays 0 for the
 * couple of seconds until the first websocket step lands — an empty bar that reads as
 * stuck. Showing a small head start (and so the sheen) makes it clear work has begun;
 * real progress takes over the moment it climbs past this.
 */
const DEPLOY_PROGRESS_FLOOR = 10;

/**
 * Finish onboarding: tear down the now-completed session, then go to the studio home.
 * The brand is live, so the configurator/customizer session is done — clearing it from
 * the store + localStorage stops it being reopened later as a stale build (a subsequent
 * "New brand" then mints a fresh session instead of resuming this paid one). The
 * just-deployed brand is still adopted on home via the separate `pendingBrandSessionId`
 * marker (written at deploy start), so clearing the session here loses nothing.
 */
const useFinishToDashboard = (): (() => void) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  return React.useCallback(() => {
    dispatch(clearSessionData());
    navigate(DASHBOARD_PATH);
  }, [dispatch, navigate]);
};

const openExternal = (url: string): void => {
  // Guard the navigation sink: only open http(s). A `javascript:` URL leaking in via the
  // unvalidated `previewUrl` fallback (deploy.domain || previewUrl) would otherwise execute
  // in the studio's own origin through window.open, with full access to the owner session.
  try {
    const { protocol } = new URL(url);
    if (protocol !== 'http:' && protocol !== 'https:') return;
  } catch {
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

const StepsList = ({
  steps,
  finished,
  label,
}: {
  steps: { text?: string }[];
  finished?: boolean;
  /** Stable accessible name for the list — supplied while deploying, where the
   *  visible heading is the decorative (aria-hidden) Lumio narration. */
  label?: string;
}): React.ReactElement => (
  <Steps role={label ? 'group' : undefined} aria-label={label}>
    {steps.map((s, i) => (
      <StepItem key={`${s.text ?? 'step'}-${i}`}>
        <StepCheck>
          <CheckIcon size={14} />
        </StepCheck>
        <StepText text={s.text ?? ''} tag="span" $finished={finished} />
      </StepItem>
    ))}
  </Steps>
);

/* ── Desktop ─────────────────────────────────────────────────────────────── */

const DesktopDeploy = (): React.ReactElement => {
  const { t } = useTranslation();
  const { country, brandName, selectedDomain, selectedLogo, previewUrl, previewTokens, deploy, deployPhase, startDeploy, goBack } =
    useCustomizer();
  const goToDashboard = useFinishToDashboard();
  const deployBlockedUnpaid = useDeployBlockedUnpaid();
  // The now-live site (custom domain once provisioned, else the configurator preview),
  // opened in a new tab by the "Preview Website" button on the done state.
  const liveUrl = deploy.domain || previewUrl || '';

  // Live "Lumio is working…" status line (deploying only) + a floored progress so the
  // bar shows a head start with its sheen the instant deploying begins (see above).
  const statusPhrases = useDeployStatusPhrases();
  const statusText = useTypewriterStatus(statusPhrases, deployPhase === 'deploying');
  const displayProgress = deployPhase === 'done' ? 100 : Math.max(DEPLOY_PROGRESS_FLOOR, deploy.progress);

  const overlayLabel =
    deployPhase === 'done'
      ? t('Your iGaming website is live.')
      : deployPhase === 'deploying'
        ? t('Setting up your iGaming brand.')
        : t('Ready to apply your preferences');

  const title =
    deployPhase === 'done'
      ? t('All Set! {{brand}} is live')
      : deployPhase === 'deploying'
        ? t('Setting up {{brand}}')
        : t('Review & Go Live');
  const titlePlaceholders = deployPhase === 'review' ? undefined : { brand: brandName ?? t('your casino') };
  const HeaderIcon = deployPhase === 'deploying' ? RocketIcon : SparkleIcon;

  return (
    <Shell>
      <HeaderRow>
        <HeaderLeft>
          {deployPhase === 'review' ? (
            // Back to the previous step so the owner can change details before going
            // live. Hidden once deploying/done; the deploy is a one-way door.
            <BackButton type="button" aria-label={t('Back')} onClick={goBack}>
              <ArrowLeftIcon size={18} />
            </BackButton>
          ) : (
            <Avatar>
              <HeaderIcon size={16} />
            </Avatar>
          )}
          <HeaderTitle text={title} placeholders={titlePlaceholders} tag="h1" />
        </HeaderLeft>
        <HeaderRight>
          {deployPhase === 'done' ? (
            <LiveRow>
              <LiveDot />
              <LiveText text="Live on {{domain}}" placeholders={{ domain: selectedDomain ?? '' }} tag="span" />
            </LiveRow>
          ) : null}
        </HeaderRight>
      </HeaderRow>

      <Body>
        <PreviewCol>
          <PreviewLabel text={overlayLabel} tag="span" />
          <DevicePreview
            previewUrl={previewUrl}
            logoUrl={selectedLogo}
            tokens={previewTokens}
            phase={deployPhase === 'review' ? 'pending' : deployPhase === 'deploying' ? 'deploying' : 'live'}
          />
        </PreviewCol>

        <Sidebar>
          <SideHeader>
            <SideHeaderRow $center={deployPhase === 'review'}>
              <LabelGroup>
                {deployPhase === 'deploying' ? <RecordDot /> : null}
                <SideHeaderLabel
                  $variant={deployPhase}
                  tag="span"
                  text={
                    deployPhase === 'review'
                      ? t('Preferences Summary')
                      : deployPhase === 'deploying'
                        ? t('Deploying')
                        : t('Completed')
                  }
                />
              </LabelGroup>
              {deployPhase !== 'review' ? (
                <Pct text="{{n}}%" placeholders={{ n: displayProgress }} tag="span" />
              ) : null}
            </SideHeaderRow>
            <Track $variant={deployPhase}>
              {deployPhase !== 'review' ? <Fill $pct={displayProgress} $done={deployPhase === 'done'} /> : null}
            </Track>
          </SideHeader>

          <SideBody $center={deployPhase === 'review'}>
            {deployPhase === 'review' ? (
              <SummaryList>
                <SummaryRow>
                  <SumLabel text="Your online casino will operate in" tag="span" />
                  <SumValueRow>
                    {country?.flag ? <FlagImg src={country.flag} alt="" /> : null}
                    <SumValue text={country?.name ?? ''} tag="span" />
                  </SumValueRow>
                </SummaryRow>
                <Divider />
                <SummaryRow>
                  <SumLabel text="… will be known as" tag="span" />
                  <SumValueRow>
                    <DiplomaIcon size={20} />
                    <SumValue text={brandName ?? ''} tag="span" />
                  </SumValueRow>
                </SummaryRow>
                <Divider />
                <SummaryRow>
                  <SumLabel text="… will be reached at" tag="span" />
                  <SumValueRow>
                    <GlobeIcon size={20} />
                    <SumValue text={selectedDomain ?? ''} tag="span" />
                  </SumValueRow>
                </SummaryRow>
                <Divider />
                <SummaryRow>
                  <SumLabel text="… will be remembered for" tag="span" />
                  {selectedLogo ? <SumLogo src={selectedLogo} alt="" /> : null}
                </SummaryRow>
              </SummaryList>
            ) : (
              <Section>
                {deployPhase === 'done' ? (
                  <DoneHeading text={t('Summary')} tag="span" />
                ) : (
                  // Live narration in Lumio's voice while deploying — replaces the
                  // static "Done so far" so the panel reads as actively working.
                  // Decorative (per-char mutation); aria-hidden so it isn't read aloud.
                  <TypingHeading aria-hidden>{statusText}</TypingHeading>
                )}
                <StepsList
                  steps={deploy.steps}
                  finished={deployPhase === 'done'}
                  label={deployPhase === 'deploying' ? t('Deployment progress') : undefined}
                />
              </Section>
            )}
          </SideBody>

          <SideFooter>
            {deployPhase === 'review' ? (
              <>
                {/* A bank-transfer (SPEI) owner can reach onboarding before their money has
                    landed, so launching has to wait for the invoice. Explains WHY rather than
                    just greying the button out. Renders nothing on the paid/unknown path. */}
                {deployBlockedUnpaid ? <UnpaidNotice /> : null}
                <Cta
                  type="button"
                  $enabled={!deployBlockedUnpaid}
                  disabled={deployBlockedUnpaid}
                  // Guard in the handler too: a styled button is still clickable.
                  onClick={() => {
                    if (deployBlockedUnpaid) return;
                    startDeploy();
                  }}
                >
                  <CtaLabel text="Finish setup" tag="span" />
                </Cta>
              </>
            ) : deployPhase === 'deploying' ? (
              <Cta type="button" $enabled={false} disabled>
                <CtaLabel text="Go to dashboard" tag="span" />
              </Cta>
            ) : (
              <>
                <PreviewCta type="button" onClick={() => liveUrl && openExternal(liveUrl)}>
                  <CtaLabel text="Preview Website" tag="span" />
                  <ExternalLinkIcon size={18} />
                </PreviewCta>
                <Cta type="button" $enabled onClick={goToDashboard}>
                  <CtaLabel text="Go to dashboard" tag="span" />
                </Cta>
              </>
            )}
          </SideFooter>
        </Sidebar>
      </Body>
    </Shell>
  );
};

/* ── Mobile ──────────────────────────────────────────────────────────────── */

const MobileDeploy = (): React.ReactElement => {
  const { t } = useTranslation();
  const { country, brandName, selectedDomain, selectedLogo, previewUrl, previewTokens, deploy, deployPhase } =
    useCustomizer();
  const goToDashboard = useFinishToDashboard();
  // Hooks must run before the per-phase early returns below.
  const deployBlockedUnpaid = useDeployBlockedUnpaid();
  const statusPhrases = useDeployStatusPhrases();
  const statusText = useTypewriterStatus(statusPhrases, deployPhase === 'deploying');

  if (deployPhase === 'done') {
    const liveUrl = deploy.domain || previewUrl || '';
    return (
      <MDoneRoot>
        <MHeading text="Your website is live 🎉" tag="h1" />
        {/* The live site renders inside the shared iPhone frame; the buttons below
            sit over a bottom-to-top fade so the phone fades out behind them. */}
        <MDonePreview>
          <DevicePreview variant="phone" previewUrl={liveUrl} logoUrl={selectedLogo} tokens={previewTokens} phase="live" />
        </MDonePreview>
        <MDoneFooter>
          <MSecondary type="button" onClick={() => liveUrl && openExternal(liveUrl)}>
            <MBtnLabel text="Preview Website" tag="span" />
          </MSecondary>
          <MPrimary type="button" $green onClick={goToDashboard}>
            <MBtnLabel text="Go to dashboard" tag="span" />
          </MPrimary>
        </MDoneFooter>
      </MDoneRoot>
    );
  }

  if (deployPhase === 'deploying') {
    // Floor the bar so it shows a head start (+ sheen) before the first WS step lands.
    const mProgress = Math.max(DEPLOY_PROGRESS_FLOOR, deploy.progress);
    return (
      <MobileBody>
        <MHeading text="Taking your website live…" tag="h1" />
        <MProgressWrap>
          <SideHeaderRow>
            <LabelGroup>
              <RecordDot />
              <SideHeaderLabel $variant="deploying" text="Deploying" tag="span" />
            </LabelGroup>
            <Pct text="{{n}}%" placeholders={{ n: mProgress }} tag="span" />
          </SideHeaderRow>
          <Track $variant="deploying">
            <Fill $pct={mProgress} $done={false} />
          </Track>
        </MProgressWrap>
        {/* Live Lumio narration, mirroring desktop (replaces the static "Done so far").
            Decorative (per-char mutation); aria-hidden so it isn't read aloud. */}
        <TypingHeading aria-hidden>{statusText}</TypingHeading>
        <MList>
          <StepsList steps={deploy.steps} label={t('Deployment progress')} />
        </MList>
      </MobileBody>
    );
  }

  // Review.
  return (
    <MobileBody>
      <MHeading text="Review your details" tag="h1" />
      <MCard>
        <MCardLabel text="Your online casino will operate in" tag="span" />
        <MCardValueRow>
          {country?.flag ? <FlagImg src={country.flag} alt="" /> : null}
          <MCardValue text={country?.name ?? ''} tag="span" />
        </MCardValueRow>
      </MCard>
      <MCard>
        <MCardLabel text="… will be known as" tag="span" />
        <MCardValueRow>
          <DiplomaIcon size={18} />
          <MCardValue text={brandName ?? ''} tag="span" />
        </MCardValueRow>
      </MCard>
      <MCard>
        <MCardLabel text="… will be reached at" tag="span" />
        <MCardValueRow>
          <GlobeIcon size={16} />
          <MCardValue text={selectedDomain ?? ''} tag="span" />
        </MCardValueRow>
      </MCard>
      <MCard>
        <MCardLabel text="… will be remembered for" tag="span" />
        {selectedLogo ? <SumLogo src={selectedLogo} alt="" /> : null}
      </MCard>
      {/* Same hold-back as desktop: a bank transfer that hasn't landed yet blocks the launch,
          so say why rather than leaving a dead grey CTA at the bottom of the screen. */}
      {deployBlockedUnpaid ? <UnpaidNotice /> : null}
      {/* The "Take your website live" CTA is rendered by MobileChrome at the shared
          fixed-bottom position (same as every other step's next button). */}
    </MobileBody>
  );
};

/** Step 4 — Deploy / "Go live": review → deploying (websocket) → done. */
export const DeployStep = (): React.ReactElement => {
  const { isMobile } = useCustomizer();
  return isMobile ? <MobileDeploy /> : <DesktopDeploy />;
};

export default DeployStep;